import { AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { getDayJsFromNgbDateStruct } from '@mwe/utils';
import { maxBirthDate, maxUmmeldedatum, MIN_BIRTHYEAR, minUmmeldedatum } from './datepicker';

export class CustomValidators {
  static readonly DEFAULT_MIN_LENGTH = 2;
  static readonly FIRMENBUCH_NR_MAX_LENGTH = 12;
  static readonly STREET_MAX_LENGTH = 100;
  static readonly METER_NUMBER_MIN_LENGTH = 4;
  static readonly CUSTOMER_NUMBER_REGEX = /^[A-Za-z0-9]+$/;
  static readonly GP_NR_REGEX = /^\d{10}$/;
  static readonly VK_NR_REGEX = /^\d{12}$/;
  static readonly ANY_LETTER_REGEX = /^[\p{Letter}\s\-.']+$/u;
  static readonly PHONE_NUMBER_REGEX = /^\+?\d{10,15}$/;
  static readonly UID_REGEX = /^[A-Z0-9]{8,12}$/;
  static readonly FIRMENBUCH_NR_REGEX = /^[A-Za-z0-9]+$/;
  static readonly HOUSE_NUMBER_STAIRCASE_REGEX = /^(?=.*[A-Za-z0-9])[A-Za-z0-9./-]+$/;
  static readonly PLZ_REGEX = /^\d{4}$/;
  static readonly CITY_REGEX = /^[a-zA-ZäöüÄÖÜß\s\-.]+$/;
  static readonly STREET_REGEX = /^[A-Za-zÄÖÜäöüß0-9 .'-]+$/;
  static readonly METER_POINT_NUMBER_REGEX = /^[A-Z0-9]{12,}$/;
  static readonly METER_NUMBER_REGEX = /^\w+$/;
  static readonly METER_COUNT_REGEX = /^\s*\d+(,\d*)?\s*$/;
  static readonly LETTERS_ONLY_REGEX = /^[a-zA-Z]+$/;
  static readonly DOOR_NUMBER_REGEX = /^[a-zA-Z0-9\s/\-.,]+$/;

  static firstNameValidator(control: AbstractControl): ValidationErrors | null {
    return CustomValidators.nameValidator(control, 'firstName');
  }

  static lastNameValidator(control: AbstractControl): ValidationErrors | null {
    return CustomValidators.nameValidator(control, 'lastName');
  }

  static companyNameValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();

    if (!value) {
      return { translationKey: `inputErrors.blank.companyName` };
    }
    if (value.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: `inputErrors.tooShort.companyName` };
    }
    return null;
  }

  static nameValidator(control: AbstractControl, fieldName: string): ValidationErrors | null {
    const value = control.value?.trim();

    if (!value) {
      return { translationKey: `inputErrors.blank.${fieldName}` };
    }
    if (value.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: `inputErrors.tooShort.${fieldName}` };
    }
    if (!CustomValidators.ANY_LETTER_REGEX.test(value)) {
      return { translationKey: `inputErrors.invalid.${fieldName}` };
    }
    return null;
  }

  static emailValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.email' };
    }
    if (Validators.email({ value } as AbstractControl)) {
      return { translationKey: 'inputErrors.invalid.email' };
    }
    return null;
  }

  static dateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      return { translationKey: 'inputErrors.blank.datePicker' };
    }
    const { day, month, year } = value;

    if (!day || !month || !year) {
      return { translationKey: 'inputErrors.invalid.datePicker' };
    }
    return null;
  }

  static birthdateValidator(control: AbstractControl): ValidationErrors | null {
    const dateValidation = CustomValidators.dateValidator(control);

    if (dateValidation) {
      return dateValidation;
    }

    const birthdate = getDayJsFromNgbDateStruct(control.value);

    if (birthdate.year() < MIN_BIRTHYEAR) {
      return { translationKey: 'inputErrors.birthdate.tooOld' };
    }

    if (birthdate.isAfter(getDayJsFromNgbDateStruct(maxBirthDate))) {
      return { translationKey: 'inputErrors.birthdate.tooYoung' };
    }
    return null;
  }

  static ummeldedatumValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      return null;
    }

    const dateValidation = CustomValidators.dateValidator(control);

    if (dateValidation) {
      return dateValidation;
    }

    const ummeldedatum = getDayJsFromNgbDateStruct(value);

    if (
      ummeldedatum.isBefore(getDayJsFromNgbDateStruct(minUmmeldedatum)) ||
      ummeldedatum.isAfter(getDayJsFromNgbDateStruct(maxUmmeldedatum))
    ) {
      return { translationKey: 'inputErrors.invalid.ummeldeDatum' };
    }

    return null;
  }

  static optionalBirthdateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    return CustomValidators.birthdateValidator(control);
  }

  static optionalPhoneNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return null;
    }
    const valueWithoutWhitespace = CustomValidators.getValueWithoutWhitespace(value);
    if (!CustomValidators.PHONE_NUMBER_REGEX.test(valueWithoutWhitespace)) {
      return { translationKey: 'inputErrors.invalid.phoneNumber' };
    }
    return null;
  }

  static uidValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.uidNr' };
    }
    const valueWithoutWhitespace = CustomValidators.getValueWithoutWhitespace(value);
    if (!CustomValidators.UID_REGEX.test(valueWithoutWhitespace) || CustomValidators.LETTERS_ONLY_REGEX.test(valueWithoutWhitespace)) {
      return { translationKey: 'inputErrors.invalid.uidNr' };
    }
    return null;
  }

  static optionalUidValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return null;
    }
    return CustomValidators.uidValidator(control);
  }

  static optionalEmailValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return null;
    }
    return CustomValidators.emailValidator(control);
  }

  static optionalFirmenbuchNrValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return null;
    }
    if (!CustomValidators.FIRMENBUCH_NR_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.firmenbuchNr' };
    }
    if (value.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: 'inputErrors.tooShort.firmenbuchNr' };
    }
    if (value.length > CustomValidators.FIRMENBUCH_NR_MAX_LENGTH) {
      return { translationKey: 'inputErrors.tooLong.firmenbuchNr' };
    }
    return null;
  }

  static plzValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.plz' };
    }
    if (!CustomValidators.PLZ_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.plz' };
    }
    return null;
  }

  static cityValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.city' };
    }
    if (value.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: 'inputErrors.tooShort.city' };
    }
    if (!CustomValidators.CITY_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.city' };
    }
    return null;
  }

  static streetValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.street' };
    }
    if (value.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: 'inputErrors.tooShort.street' };
    }
    if (value.length > CustomValidators.STREET_MAX_LENGTH) {
      return { translationKey: 'inputErrors.tooLong.street' };
    }
    if (!CustomValidators.STREET_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.street' };
    }
    return null;
  }

  static houseNumberStaircaseValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value || value?.length === 0) {
      return { translationKey: 'inputErrors.blank.houseNumberStaircase' };
    }
    if (!CustomValidators.HOUSE_NUMBER_STAIRCASE_REGEX.test(value.replaceAll(' ', ''))) {
      return {
        translationKey: 'inputErrors.invalid.houseNumberStaircase',
      };
    }
    return null;
  }

  static doorNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();

    if (!value) {
      return null;
    }

    if (!CustomValidators.DOOR_NUMBER_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.door' };
    }

    return null;
  }

  static meterPointNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return { translationKey: 'inputErrors.blank.meterPointNumber' };
    }
    const valueWithoutWhitespace = CustomValidators.getValueWithoutWhitespace(value);
    if (!CustomValidators.METER_POINT_NUMBER_REGEX.test(valueWithoutWhitespace)) {
      return { translationKey: 'inputErrors.invalid.meterPointNumber' };
    }
    return null;
  }

  static meterNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.meterNumber' };
    }
    if (!CustomValidators.METER_NUMBER_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.meterNumber' };
    }
    if (value.length < CustomValidators.METER_NUMBER_MIN_LENGTH) {
      return { translationKey: 'inputErrors.tooShort.meterNumber' };
    }
    return null;
  }

  static meterCountValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }
    if (!CustomValidators.METER_COUNT_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.meterCount' };
    }
    const valueWithoutWhitespace = CustomValidators.getValueWithoutWhitespace(value);
    if (valueWithoutWhitespace.length > 20) {
      return { translationKey: 'inputErrors.tooLong.meterCount' };
    }
    if (value < 0) {
      return { translationKey: 'inputErrors.invalid.meterCount' };
    }
    return null;
  }

  static gpNrVerificationValidator(control: AbstractControl): ValidationErrors | null {
    if (!CustomValidators.GP_NR_REGEX.test(control.value)) {
      return { translationKey: 'registration.verification.input.customerNumber.verify' };
    }
    return null;
  }

  static vkNrVerificationValidator(control: AbstractControl): ValidationErrors | null {
    if (!CustomValidators.VK_NR_REGEX.test(control.value)) {
      return { translationKey: 'registration.verification.input.accountNumber.verify' };
    }
    return null;
  }

  static customerNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value ?? '';
    const valueWithoutWhitespace = CustomValidators.getValueWithoutWhitespace(value);

    if (!valueWithoutWhitespace) {
      return { translationKey: 'inputErrors.blank.customerNumber' };
    }

    if (valueWithoutWhitespace.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: 'inputErrors.tooShort.customerNumber' };
    }

    if (!CustomValidators.CUSTOMER_NUMBER_REGEX.test(valueWithoutWhitespace)) {
      return { translationKey: 'inputErrors.invalid.customerNumber' };
    }

    return null;
  }

  static optionalCustomerNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value ?? '';
    const valueWithoutWhitespace = CustomValidators.getValueWithoutWhitespace(value);

    return valueWithoutWhitespace ? CustomValidators.customerNumberValidator(control) : null;
  }

  private static getValueWithoutWhitespace(value: string): string {
    return value?.replace(/\s/g, '');
  }
}
